import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBusinessSnapshot,
  getEvents,
  getNews,
  createEvent,
  deleteEvent,
  getWatchlist,
  addFundToWatchlist,
  removeFundFromWatchlist,
  getIndices,
  updateEvent,
  getPendingEvents,
} from "../api/home";
import { CresoEvent } from "../types/home";
import { noteKeys } from "./note";
import { NoteDetail } from "../types";

export function useBusinessSnapshot(enabled = true) {
  return useQuery({
    queryKey: ["business-snapshot"],
    queryFn: getBusinessSnapshot,
    enabled,
  });
}

export function useEvents(
  variables: { date: string; clientId?: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["events", variables.date, variables.clientId],
    queryFn: () => getEvents(variables),
    enabled: !!variables.date && (options?.enabled ?? true),
  });
}

export function useNews() {
  return useQuery({
    queryKey: ["news"],
    queryFn: getNews,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEvent,
    onSuccess: (data) => {
      if (data.note_id) {
        queryClient.setQueryData(
          noteKeys.detail(data.note_id),
          (note: NoteDetail) => {
            const tasks = note?.tasks || [];
            return {
              ...note,
              tasks: [data, ...tasks],
            };
          }
        );
      }
      const caches = queryClient.getQueriesData<CresoEvent[]>({
        queryKey: ["events"],
      });
      for (const [queryKey] of caches) {
        const [, cacheDate, cacheClientId] = queryKey as [
          string,
          string,
          number | undefined,
        ];
        const matchesDate = cacheDate === data.date || !data.date;
        const matchesClient =
          cacheClientId === undefined || cacheClientId === data.client_id;
        // eslint-disable-next-line
        if (!matchesDate || !matchesClient) continue;
        queryClient.setQueryData<CresoEvent[]>(queryKey, (existing) =>
          existing ? [data, ...existing] : [data]
        );
      }
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEvent,
    onSuccess: (data) => {
      if (data.note_id) {
        queryClient.setQueryData(
          noteKeys.detail(data.note_id),
          (note: NoteDetail) => {
            const tasks = note?.tasks || [];
            const newTasks = tasks.map((t) => (t.id === data.id ? data : t));
            return {
              ...note,
              tasks: newTasks,
            };
          }
        );
      }
      const caches = queryClient.getQueriesData<CresoEvent[]>({
        queryKey: ["events"],
      });
      for (const [queryKey] of caches) {
        const [, cacheDate, cacheClientId] = queryKey as [
          string,
          string,
          number | undefined,
        ];
        const matchesDate = cacheDate === data.date || !data.date;
        const matchesClient =
          cacheClientId === undefined || cacheClientId === data.client_id;
        queryClient.setQueryData<CresoEvent[]>(queryKey, (existing) => {
          if (!existing) return existing;
          const hasEvent = existing.some((e) => e.id === data.id);
          if (hasEvent && matchesDate)
            return existing.map((e) => (e.id === data.id ? data : e));
          if (hasEvent && !matchesDate)
            return existing.filter((e) => e.id !== data.id);
          if (!hasEvent && matchesDate && matchesClient)
            return [...existing, data];
          return existing;
        });
      }
    },
  });
}

export function useDeleteEvent(noteId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEvent(id),
    onSuccess: (_, id) => {
      if (noteId) {
        queryClient.invalidateQueries({ queryKey: noteKeys.detail(noteId) });
      }
      queryClient.setQueriesData<CresoEvent[]>(
        { queryKey: ["events"] },
        (existing) => existing?.filter((e) => e.id !== id)
      );
    },
  });
}

export function usePendingEvents(
  variables: { clientId?: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["events", "pending", variables.clientId],
    queryFn: () => getPendingEvents(variables.clientId),
    enabled: options?.enabled ?? true,
  });
}

export function useWatchlist() {
  return useQuery({
    queryKey: ["watchlist"],
    queryFn: getWatchlist,
  });
}

export function useAddFundToWatchlist() {
  return useMutation({
    mutationFn: addFundToWatchlist,
  });
}

export function useRemoveFundFromWatchlist() {
  return useMutation({
    mutationFn: removeFundFromWatchlist,
  });
}

export function useIndices() {
  return useQuery({
    queryKey: ["indices"],
    queryFn: getIndices,
    refetchInterval: 1000 * 60 * 30,
    refetchIntervalInBackground: true,
  });
}
